import { useEffect, useRef, useState } from "react";

/**
 * ASCII-style spectrum analyser. Each column is a stack of `#` glyphs;
 * heights drift toward new targets with exponential smoothing so it feels
 * like audio reacting rather than pure noise. Tops of the highest bars
 * glow amber like the reference video.
 */
export function FreqWidget({
  bars = 16,
  rows = 8,
  cell = 8,
  peakColor = "#ffb020",
  barColor = "#ff2d92",
  baseColor = "rgba(255,45,146,0.25)",
  label = "FREQ",
}: {
  bars?: number;
  rows?: number;
  /** Pixel size of each ASCII cell — shrink for tight layouts. */
  cell?: number;
  peakColor?: string;
  barColor?: string;
  baseColor?: string;
  label?: string;
}) {
  const [heights, setHeights] = useState<number[]>(
    () => new Array(bars).fill(0).map(() => Math.random() * rows)
  );
  const targetsRef = useRef<number[]>(
    new Array(bars).fill(0).map(() => Math.random() * rows)
  );
  const frameRef = useRef(0);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();

    function tick(now: number) {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      frameRef.current++;

      // Re-roll targets every ~180ms so the bars keep dancing.
      if (frameRef.current % 11 === 0) {
        targetsRef.current = targetsRef.current.map(() => {
          // Weighted toward middle heights; occasional spike.
          const r = Math.random();
          if (r > 0.92) return rows * 0.95;
          if (r > 0.6) return rows * (0.5 + Math.random() * 0.5);
          return rows * Math.random() * 0.7;
        });
      }

      setHeights((prev) =>
        prev.map((h, i) => {
          const t = targetsRef.current[i];
          // Exponential smoothing — slower rise, quick fall feels right.
          const k = t > h ? 6 : 10;
          return h + (t - h) * Math.min(1, k * dt);
        })
      );

      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [rows]);

  const CELL = cell;
  return (
    <div className="flex items-end gap-2 font-mono text-[9px]">
      <div className="uppercase tracking-[0.2em] text-accent2">{label}</div>
      <div
        className="flex items-end gap-[2px]"
        style={{ height: rows * CELL + 4 }}
        aria-hidden="true"
      >
        {heights.map((h, i) => {
          const filled = Math.max(1, Math.round(h));
          const cells: React.ReactNode[] = [];
          for (let r = 0; r < rows; r++) {
            const fromTop = rows - 1 - r;
            const active = fromTop < filled;
            const isPeak = active && fromTop === filled - 1 && filled >= rows * 0.65;
            cells.push(
              <span
                key={r}
                style={{
                  color: isPeak ? peakColor : active ? barColor : "transparent",
                  textShadow: isPeak
                    ? `0 0 6px ${peakColor}`
                    : active
                    ? `0 0 4px ${barColor}`
                    : "none",
                  display: "block",
                  lineHeight: `${CELL}px`,
                  height: `${CELL}px`,
                  fontSize: `${CELL}px`,
                  width: `${CELL}px`,
                  textAlign: "center",
                  overflow: "hidden",
                }}
              >
                #
              </span>
            );
          }
          // Baseline tick under every column.
          return (
            <div key={i} className="flex flex-col items-center">
              {cells}
              <span
                style={{
                  color: baseColor,
                  lineHeight: "4px",
                  height: "4px",
                  fontSize: "5px",
                }}
              >
                ·
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
